/**
 * PSC Widget Configuration Types
 */

export type WidgetPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

export interface UserInfo {
  id: string;
  role: string;
  email?: string;
  name?: string;
}

export interface PSCWidgetConfig {
  /** Application name displayed in submissions (e.g., "Treasury", "Risk") */
  appName: string;

  /** SPC backend API base URL */
  apiBaseUrl: string;

  /** Widget button position (default: 'bottom-right') */
  position?: WidgetPosition;

  /** App-specific categories for requirements */
  categories?: string[];

  /** Capture console errors automatically (default: true) */
  captureConsoleErrors?: boolean;

  /** Capture failed network requests (default: true) */
  captureNetworkErrors?: boolean;

  /** Capture selected entity context (default: true) */
  captureEntities?: boolean;

  /** Maximum number of console errors to store (default: 50) */
  maxConsoleErrors?: number;

  /** Maximum number of network errors to store (default: 20) */
  maxNetworkErrors?: number;

  /** Capture session recording using rrweb (default: true) */
  captureSessionRecording?: boolean;

  /** Session recording buffer duration in seconds (default: 30) */
  recordingBufferDuration?: number;

  /** Regex patterns to redact from captured context */
  redactPatterns?: RegExp[];

  /** Callback when submission succeeds */
  onSubmitSuccess?: (requirementId: string) => void;

  /** Callback when submission fails */
  onSubmitError?: (error: Error) => void;

  /** Function to get current auth token */
  getAuthToken?: () => string | Promise<string>;

  /** Function to get current user info */
  getUserInfo?: () => UserInfo | null;

  /** Function to get currently selected entities in the app */
  getSelectedEntities?: () => EntityContext[];

  /** Custom CSS class for the floating button */
  buttonClassName?: string;

  /** Custom CSS class for the dialog */
  dialogClassName?: string;

  /** Z-index for the widget (default: 9999) */
  zIndex?: number;

  /** Whether the widget is enabled (default: true) */
  enabled?: boolean;

  /** Keyboard shortcut to open widget (default: 'ctrl+shift+f') */
  keyboardShortcut?: string;
}

export interface EntityContext {
  type: string;
  id: string;
  name?: string;
  metadata?: Record<string, unknown>;
}

export interface ResolvedConfig extends Required<Omit<PSCWidgetConfig, 'getAuthToken' | 'getUserInfo' | 'getSelectedEntities' | 'onSubmitSuccess' | 'onSubmitError' | 'redactPatterns'>> {
  getAuthToken?: () => string | Promise<string>;
  getUserInfo?: () => UserInfo | null;
  getSelectedEntities?: () => EntityContext[];
  onSubmitSuccess?: (requirementId: string) => void;
  onSubmitError?: (error: Error) => void;
  redactPatterns: RegExp[];
}

export const DEFAULT_CONFIG: Omit<ResolvedConfig, 'appName' | 'apiBaseUrl'> = {
  position: 'bottom-right',
  categories: [],
  captureConsoleErrors: true,
  captureNetworkErrors: true,
  captureEntities: true,
  captureSessionRecording: true,
  maxConsoleErrors: 50,
  maxNetworkErrors: 20,
  recordingBufferDuration: 30,
  redactPatterns: [],
  buttonClassName: '',
  dialogClassName: '',
  zIndex: 9999,
  enabled: true,
  keyboardShortcut: 'ctrl+shift+f',
};
